import { ReactElement, useMemo } from 'react';

import { useAppSelector } from '../../store/hooks';
import { RootState } from '../../store/rootState';

import { makeSelectTabsForWindow, selectWindow, Tab } from '../../store/windowsSlice';
import 'date-fns';
import WindowTabListHeader from './WindowTabListHeader';
import { Page, PageFilter, fetchPagesByNormalizedUrls } from '../../db/pages';
import PageRow from '../flexView/pagelists/pagerow/PageRow';
import SortablePageRow from './SortablePageRow';
import { useLiveQuery } from 'dexie-react-hooks';
import { NormalizedUrl } from '../../chrome/normalizeUrl';
import React from 'react';
import { filterPage } from '../sort';
import { shouldIgnore } from '../../db/urlNormalizationPrefs';
import { sortBy } from 'lodash';
import { pageSortOrderMap } from '../sort';
import { PageSortOrder } from '../../db/pages';
import { selectSimilarUrls, selectSelectedUrls } from '../../store/uiSlice';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

function syntheticPageFromTab(tab: Tab): Page {
  return {
    nurl: tab.url!,
    title: tab.title,
    visible: 'active',
    accessTimes: [],
    lastActiveDesc: 0,
    hotnessDesc: 0,
    humanTags: [],
    humanRejectedTags: [],
    autoTags: [],
    openerUrls: [],
    openedUrls: [],
    timeAdjacentUrls: [],
  };
}

interface WindowRowProps {
  windowId: number;
  filter: PageFilter;
  sortOrder: PageSortOrder;
  dragItemIds?: number[];
  isDragEnabled?: boolean;
  activeDragId?: number | null;
}

const WindowTabList = React.memo(({ windowId, filter, sortOrder, dragItemIds, isDragEnabled, activeDragId }: WindowRowProps): ReactElement => {
  const selectTabsForWindow = useMemo(makeSelectTabsForWindow, []);

  const window = useAppSelector((state: RootState) => selectWindow(state, windowId));
  const tabs = useAppSelector((state: RootState) => selectTabsForWindow(state, windowId));
  const similarUrls = useAppSelector(selectSimilarUrls);
  const selectedUrls = useAppSelector(selectSelectedUrls);
  const urls = useMemo(() => tabs.map((tab) => tab.url).filter(Boolean) as NormalizedUrl[], [tabs]);
  const pages = useLiveQuery(() => fetchPagesByNormalizedUrls(urls), [urls]);
  if (!tabs || tabs.length === 0 || pages === undefined) {
    return <></>;
  }

  const normalizedUrlToPage: { [key: string]: Page } = pages
    ? Object.fromEntries(pages.map((page) => [page.nurl.normalized, page]))
    : {};

  if (window === undefined || tabs === undefined || urls === undefined || pages === undefined) {
    return <></>;
  }

  const tabById: { [id: number]: (typeof tabs)[0] } = Object.fromEntries(
    tabs.filter(t => t.id !== undefined).map((t) => [t.id!, t])
  );

  if (isDragEnabled && dragItemIds !== undefined) {
    return (
      <div
        style={{
          border: '1px solid',
          margin: '0.5rem 0.5rem 0.5rem 0.5rem',
          borderColor: 'rgb(50, 56, 62)',
        }}
      >
        <WindowTabListHeader accessTime={window.accessTime} tabs={tabs} urls={urls} />
        <SortableContext items={dragItemIds} strategy={verticalListSortingStrategy}>
          {dragItemIds.map((tabId) => {
            const tab = tabById[tabId];
            if (!tab || !tab.url) {
              // Show a height placeholder only during an active drag (reserves space for the
              // dragged item in the target window). For loading/untracked tabs, render nothing.
              return activeDragId != null ? <div key={tabId} style={{ height: '1.4rem' }} /> : null;
            }
            const nurl = tab.url.normalized;
            const page = normalizedUrlToPage[nurl];
            if (!page) {
              if (filter.flagged) return <div key={tabId} style={{ height: '1.4rem' }} />;
              if (!shouldIgnore(nurl)) return null;
              return (
                <div key={tabId} style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ cursor: 'default', color: '#333', padding: '2px 4px', userSelect: 'none', flexShrink: 0, fontSize: '0.75rem', lineHeight: 1 }}>⠿</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <PageRow page={syntheticPageFromTab(tab)} windowId={windowId} tabId={tabId} ignoredMode />
                  </div>
                </div>
              );
            }
            if (!filterPage(page, filter)) {
              return <div key={tabId} style={{ height: '1.4rem' }} />;
            }
            return (
              <SortablePageRow
                key={tabId}
                id={tabId}
                page={page}
                windowId={windowId}
                tabId={tabId}
              />
            );
          })}
        </SortableContext>
      </div>
    );
  }

  let tabsToRender = tabs;

  if (sortOrder === PageSortOrder.similarity) {
       tabsToRender = sortBy(tabs, [
          (tab) => !selectedUrls.includes(tab.url?.normalized || ''),
          (tab) => {
              const nurl = tab.url?.normalized;
              return -(similarUrls[nurl || ''] || 0);
          }
      ]);
  } else if (sortOrder !== PageSortOrder.recent) {
      tabsToRender = sortBy(tabs, (tab) => {
          const nurl = tab.url?.normalized;
          const page = nurl ? normalizedUrlToPage[nurl] : undefined;
          if (!page) return 0;
          return pageSortOrderMap[sortOrder](page);
      });
  }

  return (
    <div
      style={{
        border: '1px solid',
        margin: '0.5rem 0.5rem 0.5rem 0.5rem',
        borderColor: 'rgb(50, 56, 62)',
      }}
    >
      <WindowTabListHeader accessTime={window.accessTime} tabs={tabs} urls={urls} />

      {tabsToRender &&
        tabsToRender.map((tab) => {
          const normalizedUrl = tab.url?.normalized;
          const page = normalizedUrl ? normalizedUrlToPage[normalizedUrl] : undefined;
          if (!page) {
            if (filter.flagged) return <div key={tab.id}></div>;
            if (!tab.url || !shouldIgnore(tab.url.normalized)) return null;
            return <PageRow key={tab.id} page={syntheticPageFromTab(tab)} windowId={windowId} tabId={tab.id} ignoredMode />;
          }
          if (!filterPage(page, filter)) {
            return <div key={tab.id}></div>;
          }
          return <PageRow key={tab.id} page={page} windowId={windowId} tabId={tab.id} />;
        })}
    </div>
  );
});

export default WindowTabList;
